import { useTranslation } from 'next-i18next';

import { Flex, Skeleton, Slider, Stack, Text } from '@chakra-ui/react';

import { Input } from '@nocowanie/common-ui/components/atoms/input';

import { FilterRangeSliderProps } from '@app/components/molecules/filter-range-slider';
import { useRangeSlider } from '@app/hooks/useRangeSlider';

export const FilterRangeSlider = ({
    title,
    min,
    max,
    step = 1,
    unit = '',
    selectedRange,
    onChange,
    isLoading = false,
}: FilterRangeSliderProps) => {
    const { t } = useTranslation('common', { keyPrefix: 'filters' });

    const { inputValues, localRange, getInputProps, handleValueChange, handleValueChangeEnd } =
        useRangeSlider({
            min,
            max,
            step,
            selectedRange,
            onChange,
        });

    const renderInput = (label: string, id: string, index: 0 | 1) => (
        <Input
            id={id}
            label={label}
            inputProps={{
                value: inputValues[index],
                'data-testid': `range-slider-input-${id}`,
                ...getInputProps(index),
            }}
            rightElement={unit}
            rightElementProps={{ pl: 1 }}
            showValidationIcons={false}
        />
    );

    return (
        <Stack gap={4} data-testid="range-slider">
            {title ? (
                <Skeleton loading={isLoading} w={'160px'} asChild>
                    <Text fontWeight="bold">{title}</Text>
                </Skeleton>
            ) : null}

            {isLoading ? (
                <Skeleton loading={isLoading} minH={'20px'} />
            ) : (
                <Slider.Root
                    min={min}
                    max={max}
                    step={step}
                    value={localRange}
                    onValueChange={handleValueChange}
                    onValueChangeEnd={handleValueChangeEnd}
                >
                    <Slider.Control cursor={'pointer'} data-testid="range-slider-slider">
                        <Slider.Track h={'2px'}>
                            <Slider.Range />
                        </Slider.Track>
                        <Slider.Thumbs opacity={isLoading ? 0 : undefined} />
                    </Slider.Control>
                </Slider.Root>
            )}

            <Skeleton loading={isLoading} asChild>
                <Flex mt={1} gap={3} align="center" data-testid="range-slider-inputs">
                    {renderInput(t('minValue'), 'minPrice', 0)}
                    <Text as="span" color={'gray.400'} mb={3} alignSelf={'flex-end'}>
                        –
                    </Text>
                    {renderInput(t('maxValue'), 'maxPrice', 1)}
                </Flex>
            </Skeleton>
        </Stack>
    );
};
